import { apiCall } from 'modules/core/services/api'
import { FeatureFlagsService } from 'openapi'
import { create } from 'zustand'

/** Waits before each retry of a failed load; a failure after the last one settles to fallbacks. */
export const BUSINESS_FLAGS_RETRY_DELAYS_MS = [1_000, 3_000]

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

/**
 * Business-scoped flags (`scope: 'business'`), evaluated by our server for the
 * request's business. posthog-js can't say which business a `/flags` response
 * belongs to, so a response in flight during a business switch would be read
 * as the new business's answer. Here every load records the business it was
 * requested for: a load that a newer one superseded is dropped, and a response
 * the server evaluated for a different business than requested settles to
 * fallbacks instead of being trusted.
 */
interface BusinessFlagsState {
  /** The business the latest load was requested for. */
  requestedFor: string | null
  /** The business `flags` belong to; null until a load for it has finished. */
  settledFor: string | null
  flags: Record<string, boolean>
  load: (businessId: string) => Promise<void>
  reset: () => void
}

export const useBusinessFlagsStore = create<BusinessFlagsState>((set, get) => {
  const isCurrent = (businessId: string) => get().requestedFor === businessId

  return {
    requestedFor: null,
    settledFor: null,
    flags: {},

    load: async (businessId: string) => {
      set({ requestedFor: businessId })
      for (let attempt = 0; ; attempt++) {
        try {
          const response = await apiCall(FeatureFlagsService.featureFlagsControllerBusinessV1, null)
          if (!isCurrent(businessId)) return // a newer load owns the state
          if (response.businessId !== businessId) {
            // The session's token is scoped to another business than the UI shows
            console.error(
              `Business feature flags evaluated for ${response.businessId}, requested for ${businessId}`
            )
            set({ settledFor: businessId, flags: {} })
            return
          }
          set({ settledFor: businessId, flags: response.flags })
          return
        } catch (error) {
          if (!isCurrent(businessId)) return
          const delay = BUSINESS_FLAGS_RETRY_DELAYS_MS[attempt]
          if (delay === undefined) {
            console.error('Failed to load business feature flags:', error)
            // Settle with no values so every business flag reads its fallback
            set({ settledFor: businessId, flags: {} })
            return
          }
          await sleep(delay)
          if (!isCurrent(businessId)) return
        }
      }
    },

    reset: () => set({ requestedFor: null, settledFor: null, flags: {} }),
  }
})
